import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Rocket, Terminal, Heart, Mail, ArrowUpRight, ChevronDown } from "lucide-react";
import { SIGNAL } from "@/content";
import { EVA_LUNA } from "@/status";
import StatusBar from "@/components/StatusBar";

const PATHS = [
  {
    id: "cofounder",
    icon: Rocket,
    label: "I need a co-founder",
    accent: "#0066FF",
    accentBg: "rgba(0,102,255,0.07)",
    accentBorder: "rgba(0,102,255,0.25)",
    expanded:
      "I bring product, design, engineering, and commercial thinking. I've done this three times. Let's talk about your idea.",
    cta: "Let's talk",
    playful: false,
  },
  {
    id: "consultancy",
    icon: Terminal,
    label: "I need consultancy",
    accent: "#00D4AA",
    accentBg: "rgba(0,212,170,0.07)",
    accentBorder: "rgba(0,212,170,0.25)",
    expanded:
      "Product leadership, technical architecture, and team management on a flexible basis. Currently taking on select engagements.",
    cta: "Start the conversation",
    playful: false,
  },
  {
    id: "cat",
    icon: Heart,
    label: "I just want to see the cat",
    accent: "#A855F7",
    accentBg: "rgba(168,85,247,0.08)",
    accentBorder: "rgba(168,85,247,0.30)",
    expanded:
      "Good choice. This is Eva Luna — Chief Vibe Officer. Every day I point a camera at her and beg her to do one (1) cute thing on cue so she goes viral and I can finally quit my job. Every day she blinks slowly, judges me, and walks off. Follow her anyway — you might be the like that sets me free.",
    cta: "Fine. Let's talk too.",
    playful: true,
  },
];

function PathCard({ path, isOpen, onClick, onCta }) {
  const Icon = path.icon;

  return (
    <motion.div
      layout
      className="relative flex-1 min-w-0 rounded-[20px] overflow-hidden border bg-white cursor-pointer select-none"
      style={{
        borderColor: isOpen ? path.accentBorder : "rgba(0,0,0,0.08)",
        transition: "border-color 0.3s ease",
      }}
      whileHover={
        path.playful
          ? { scale: 1.015, animation: "none" }
          : { scale: 1.012 }
      }
      whileTap={{ scale: 0.99 }}
      data-cursor="magnetic"
      onClick={onClick}
    >
      {/* Playful card gets a subtle purple gradient bg */}
      {path.playful && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 80% 20%, rgba(168,85,247,0.09), transparent 65%)",
          }}
        />
      )}

      {/* Wobble wrapper for playful card */}
      <div
        className={path.playful ? "playful-card-inner" : ""}
        style={path.playful ? { display: "contents" } : {}}
      >
        <div className="p-7 md:p-9">
          {/* Icon + toggle row */}
          <div className="flex items-start justify-between gap-4">
            <div
              className="w-12 h-12 rounded-[14px] flex items-center justify-center shrink-0"
              style={{ background: path.accentBg, border: `1px solid ${path.accentBorder}` }}
            >
              <Icon size={22} strokeWidth={1.8} style={{ color: path.accent }} />
            </div>

            <motion.div
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="mt-1 shrink-0"
            >
              <ChevronDown
                size={18}
                strokeWidth={2}
                style={{ color: isOpen ? path.accent : "var(--color-mute)" }}
              />
            </motion.div>
          </div>

          <h3 className="mt-5 font-serif text-[22px] md:text-[26px] leading-[1.15] tracking-tight text-ink">
            {path.label}
          </h3>

          {/* Expanded content */}
          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.div
                key="expanded"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                style={{ overflow: "hidden" }}
              >
                <div className="pt-5">
                  {/* Cat photo */}
                  {path.playful && (
                    <div className="mb-5 rounded-[14px] overflow-hidden border border-black/10">
                      <img
                        src="/images/eva-luna.jpg"
                        alt="Eva Luna — Sam's cat and Chief Vibe Officer"
                        className="w-full h-auto block object-cover max-h-[280px]"
                        style={{ objectPosition: "center 20%" }}
                        onError={(e) => {
                          // Fallback to ASCII art if photo not found
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "block";
                        }}
                      />
                      <pre
                        className="hidden font-mono text-[12px] p-5 leading-[1.5]"
                        style={{ color: path.accent, background: path.accentBg }}
                      >
                        {`   /\\_____/\\
  /  o   o  \\
 ( ==  ^  == )
  )         (
 ( (  )   (  ) )
(__(__)___(__)__)

Eva Luna · Chief Vibe Officer`}
                      </pre>
                    </div>
                  )}

                  <p className="text-ink/75 leading-[1.65] text-[15px] md:text-[16px]">
                    {path.expanded}
                  </p>

                  {/* Eva Luna Instagram link for the cat card */}
                  {path.playful && (
                    <a
                      href={EVA_LUNA.instagram}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.18em] uppercase hover:opacity-70 transition-opacity"
                      style={{ color: path.accent }}
                    >
                      {EVA_LUNA.handle}
                      <ArrowUpRight size={11} strokeWidth={2.4} />
                    </a>
                  )}

                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onCta(path.label); }}
                    data-cursor="magnetic"
                    className="mt-5 inline-flex items-center gap-2.5 px-5 py-3 rounded-full font-mono text-[11px] tracking-[0.2em] uppercase font-semibold text-white transition-all hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    style={{ background: path.accent }}
                  >
                    <Mail size={13} strokeWidth={2.2} />
                    {path.cta}
                    <ArrowUpRight size={13} strokeWidth={2.4} />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Bottom accent bar */}
        <motion.div
          className="h-[3px] w-full"
          animate={{ opacity: isOpen ? 1 : 0, scaleX: isOpen ? 1 : 0.4 }}
          transition={{ duration: 0.3 }}
          style={{ background: `linear-gradient(90deg, ${path.accent}, transparent)`, transformOrigin: "left" }}
        />
      </div>
    </motion.div>
  );
}

export default function Signal() {
  const [openPath, setOpenPath] = useState(null);

  const toggle = (id) => setOpenPath((cur) => (cur === id ? null : id));
  const openOverlay = (subject) => {
    window.dispatchEvent(new CustomEvent("erpik:open-contact", { detail: { subject } }));
  };

  return (
    <section
      id="signal"
      className="chapter relative pt-24 md:pt-36 pb-16 md:pb-20"
      data-testid="chapter-signal"
    >
      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 w-full">

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14 md:mb-20"
        >
          <div className="label-mono mb-6">05 — {SIGNAL.kicker}</div>
          <h2 className="font-serif text-[44px] md:text-[68px] lg:text-[88px] leading-[1.02] tracking-tight text-ink">
            What kind of{" "}
            <span className="italic text-mute">trouble</span>{" "}
            are you in?
          </h2>
          <p className="mt-6 max-w-[54ch] text-mute text-[16px] md:text-[18px] leading-[1.65]">
            Pick a door. Don't overthink it.
          </p>
        </motion.div>

        {/* Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col md:flex-row gap-4 md:gap-6 items-stretch"
        >
          {PATHS.map((path, i) => (
            <motion.div
              key={path.id}
              className="flex-1"
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.75, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              style={
                path.playful
                  ? {
                      animation:
                        openPath !== path.id
                          ? "none"
                          : undefined,
                    }
                  : undefined
              }
            >
              {/* Wobble wrapper only for cat card when not open */}
              <div
                className={`h-full ${path.playful && openPath !== path.id ? "hover-wobble" : ""}`}
              >
                <PathCard
                  path={path}
                  isOpen={openPath === path.id}
                  onClick={() => toggle(path.id)}
                  onCta={openOverlay}
                />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Availability pill */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="mt-10 inline-flex items-center gap-3 px-5 py-3 rounded-full border-2 border-emerald-500/40 bg-emerald-500/8 shadow-sm"
          data-testid="availability-indicator"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
          <span className="font-mono text-[12px] tracking-[0.22em] uppercase text-emerald-800 font-semibold">
            {SIGNAL.availability}
          </span>
        </motion.div>

        {/* Status bar — sits immediately above the footer rule */}
        <StatusBar className="mt-16 md:mt-24" />

        {/* Footer */}
        <div className="mt-6 pt-8 border-t border-black/10 flex flex-wrap items-center justify-between gap-6">
          <span className="font-mono text-[11px] tracking-[0.24em] uppercase text-mute">
            SAM ERPIK © {new Date().getFullYear()}
          </span>
          <nav className="flex flex-wrap gap-6">
            {SIGNAL.footerLinks.map((l) =>
              l.href.startsWith("mailto:") ? (
                <button
                  key={l.label}
                  type="button"
                  onClick={() => openOverlay("General enquiry")}
                  data-cursor="magnetic"
                  className="font-mono text-[11px] tracking-[0.22em] uppercase text-mute hover:text-ink transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                  data-testid={`footer-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </button>
              ) : (
                <a
                  key={l.label}
                  href={l.href}
                  target={l.href.startsWith("http") ? "_blank" : undefined}
                  rel={l.href.startsWith("http") ? "noreferrer" : undefined}
                  className="font-mono text-[11px] tracking-[0.22em] uppercase text-mute hover:text-ink transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                  data-testid={`footer-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              )
            )}
          </nav>
        </div>
      </div>

    </section>
  );
}
